import React, { useState, useEffect, useCallback } from 'react';

import {
  useCheckInternalTicketAccess,
  useGetInternalTickets,
  useCreateInternalTicket,
  useGetInternalTicketDetail,
} from 'actions/internal-tickets';

import { initPubnub, teardown } from 'screens/support-agent/pubnub-manager';
import Icon from 'screens/support-agent/components/icon';
import { ERROR_AUTO_CLEAR_MS } from 'screens/support-agent/constants';
import { getCookie } from 'utils';

import { SIMPLIFIED_STATUS, CLOSED_STATUSES, ROLE_LABELS } from './constants';
import {
  Workspace,
  TopBar,
  SectionLabel,
  SearchRow,
  SearchInput,
  BtnPrimary,
  BtnSecondary,
  LeftPanel,
  TicketListScroll,
  TicketCard,
  TicketCardHead,
  TicketStatus,
  TicketTime,
  TicketId,
  TicketPreviewLine,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptySub,
  EmptySubNarrow,
  ErrorMsg,
  CreateFooter,
  CreateHint,
  CreateBtn,
  CreateFormPane,
  CreateFormInner,
  CreateFormTitle,
  CreateFormSub,
  CreateFormInput,
  CreateFormError,
  CreateFormActions,
  RoleChoiceCard,
  RoleChoiceTitle,
  RoleChoiceSub,
  RoleChoiceRow,
  RolePill,
  NoAccessWrap,
  SA_COLORS,
} from './styled-comps';
import ThreadView from './components/thread-view';

const formatTime = ts => {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const d = new Date(ms);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// ── Ticket List Item ─────────────────────────────────────────────────

const TicketListItem = ({ ticket, active, onClick }) => {
  const status = (ticket.status || '').toUpperCase();
  const displayStatus = SIMPLIFIED_STATUS[status] || status;
  const isClosed = CLOSED_STATUSES.includes(status);

  return (
    <TicketCard $active={active} onClick={onClick}>
      <TicketCardHead>
        <TicketStatus $closed={isClosed}>{displayStatus}</TicketStatus>
        <TicketTime>{formatTime(ticket.created_on_ts)}</TicketTime>
      </TicketCardHead>
      <TicketPreviewLine $closed={isClosed}>
        {ticket.last_message_preview || `Ticket #${(ticket.id || '').slice(0, 8)}`}
      </TicketPreviewLine>
      <TicketId>#{(ticket.id || '').slice(0, 8)}</TicketId>
    </TicketCard>
  );
};

// ── Main Screen ──────────────────────────────────────────────────────

const InternalTickets = () => {
  const [userId, setUserId] = useState('');
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormUserId, setCreateFormUserId] = useState('');
  const [roleChoice, setRoleChoice] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  // --- Access check ---
  const {
    data: accessData,
    get: checkAccess,
    isProcessed: accessProcessed,
    isProcessingFailed: accessFailed,
    error: accessError,
  } = useCheckInternalTicketAccess();
  useEffect(() => {
    // 'check' is the rest-api store key, not a URL param — keep it non-empty.
    checkAccess('check');
  }, [checkAccess]);

  const hasAccess = accessData?.has_access;

  // --- Init PubNub ---
  useEffect(() => {
    const uid = getCookie('user_id') || getCookie('emp_id');
    if (uid) initPubnub(uid);
    return () => teardown();
  }, []);

  // --- Tickets list ---
  const {
    data: ticketsData,
    getAll: getTickets,
    isProcessing: ticketsLoading,
    isProcessingFailed: ticketsFailed,
    error: ticketsError,
  } = useGetInternalTickets();

  const tickets = ticketsData?.tickets || [];

  // --- Ticket detail ---
  const {
    data: ticketDetailData,
    get: getTicketDetail,
    isStale: ticketDetailStale,
    isProcessing: ticketDetailProcessing,
    error: ticketDetailError,
  } = useGetInternalTicketDetail();

  const ticketDetail = ticketDetailData?.ticket || ticketDetailData || null;

  useEffect(() => {
    if (activeTicketId) {
      getTicketDetail(activeTicketId);
    }
  }, [activeTicketId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ticketDetailStale && !ticketDetailProcessing && activeTicketId) {
      getTicketDetail(activeTicketId);
    }
  }, [ticketDetailStale, ticketDetailProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Create ticket ---
  const { post: createTicket, isProcessing: creating } = useCreateInternalTicket({
    onComplete: (error, data) => {
      if (error) {
        setCreateError(typeof error === 'string' ? error : 'Failed to create ticket');
      } else if (data?.role_selection_required) {
        setCreateError(null);
        setRoleChoice({ roles: data.roles || ['PARENT', 'TEACHER'] });
        setSelectedRole(null);
      } else {
        setCreateError(null);
        setShowCreateForm(false);
        setRoleChoice(null);
        setSelectedRole(null);
        const submittedUserId = createFormUserId.trim();
        setCreateFormUserId('');
        const newTicket = data?.ticket;
        if (newTicket?.id) {
          setActiveTicketId(newTicket.id);
        }
        const createdUserId = newTicket?.user_id || submittedUserId;
        if (createdUserId) {
          setUserId(createdUserId);
          setHasSearched(true);
          getTickets({ user_id: createdUserId });
        }
      }
    },
  });

  // Auto-clear errors
  useEffect(() => {
    if (!createError) return;
    const timer = setTimeout(() => setCreateError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [createError]);

  useEffect(() => {
    if (!searchError) return;
    const timer = setTimeout(() => setSearchError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [searchError]);

  const handleSearch = useCallback(() => {
    const trimmed = userId.trim();
    if (!trimmed) {
      setSearchError('Enter a user ID to search');
      return;
    }
    setSearchError(null);
    setActiveTicketId(null);
    setHasSearched(true);
    getTickets({ user_id: trimmed });
  }, [userId, getTickets]);

  const handleOpenCreateForm = useCallback(() => {
    setActiveTicketId(null);
    setCreateFormUserId(userId.trim());
    setShowCreateForm(true);
    setCreateError(null);
    setRoleChoice(null);
    setSelectedRole(null);
  }, [userId]);

  const handleSubmitCreate = useCallback(() => {
    const trimmed = createFormUserId.trim();
    if (!trimmed) {
      setCreateError('Enter a user ID');
      return;
    }
    if (roleChoice && !selectedRole) return;
    createTicket({
      user_id: trimmed,
      ...(selectedRole ? { user_type: selectedRole } : {}),
    });
  }, [createFormUserId, roleChoice, selectedRole, createTicket]);

  const handleCancelCreate = useCallback(() => {
    setShowCreateForm(false);
    setCreateFormUserId('');
    setCreateError(null);
    setRoleChoice(null);
    setSelectedRole(null);
  }, []);

  const handleSearchKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  // --- Access gate ---
  if (accessFailed) {
    return (
      <NoAccessWrap>
        <Icon name="lock" size={48} color={SA_COLORS.TEXT_TERTIARY} />
        <EmptyTitle>Couldn&apos;t verify access</EmptyTitle>
        <EmptySub>
          {typeof accessError === 'string'
            ? accessError
            : 'Something went wrong checking your access. Please refresh and try again.'}
        </EmptySub>
      </NoAccessWrap>
    );
  }

  if (!accessProcessed) {
    return (
      <NoAccessWrap>
        <EmptySub>Checking access...</EmptySub>
      </NoAccessWrap>
    );
  }

  if (hasAccess !== true) {
    return (
      <NoAccessWrap>
        <Icon name="lock" size={48} color={SA_COLORS.TEXT_TERTIARY} />
        <EmptyTitle>Access Denied</EmptyTitle>
        <EmptySub>You do not have permission to use internal tickets.</EmptySub>
      </NoAccessWrap>
    );
  }

  const searchedId = userId.trim();

  return (
    <Workspace>
      <LeftPanel>
        <TopBar>
          <SectionLabel>Find existing tickets</SectionLabel>
          <SearchRow>
            <SearchInput
              value={userId}
              onChange={e => setUserId(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by User ID"
            />
            <BtnPrimary onClick={handleSearch} disabled={ticketsLoading || !searchedId}>
              Search
            </BtnPrimary>
          </SearchRow>
        </TopBar>

        {((!showCreateForm && createError) || searchError) && (
          <ErrorMsg>{(!showCreateForm && createError) || searchError}</ErrorMsg>
        )}

        <TicketListScroll>
          {!hasSearched && !ticketsLoading && (
            <EmptyState>
              <EmptyIcon>
                <Icon name="search" size={22} color={SA_COLORS.TEXT_TERTIARY} />
              </EmptyIcon>
              <EmptyTitle>Search for a user</EmptyTitle>
              <EmptySubNarrow>
                Look up a User ID above to view their existing tickets here.
              </EmptySubNarrow>
            </EmptyState>
          )}

          {ticketsLoading && (
            <EmptyState>
              <EmptySub>Loading tickets...</EmptySub>
            </EmptyState>
          )}

          {ticketsFailed && !ticketsLoading && (
            <EmptyState>
              <EmptySub>
                {typeof ticketsError === 'string'
                  ? ticketsError
                  : 'Failed to load tickets. Please try again.'}
              </EmptySub>
            </EmptyState>
          )}

          {hasSearched && !ticketsLoading && !ticketsFailed && (
            <>
              {tickets.length === 0 ? (
                <EmptyState>
                  <EmptySub>No tickets found for this user.</EmptySub>
                </EmptyState>
              ) : (
                tickets.map(ticket => (
                  <TicketListItem
                    key={ticket.id}
                    ticket={ticket}
                    active={ticket.id === activeTicketId}
                    onClick={() => setActiveTicketId(ticket.id)}
                  />
                ))
              )}
            </>
          )}
        </TicketListScroll>

        <CreateFooter>
          <CreateHint>Not looking up a user?</CreateHint>
          <CreateBtn onClick={handleOpenCreateForm} disabled={creating}>
            + Create new ticket
          </CreateBtn>
        </CreateFooter>
      </LeftPanel>

      {showCreateForm ? (
        <CreateFormPane>
          <CreateFormInner>
            <CreateFormTitle>New ticket</CreateFormTitle>
            <CreateFormSub>Enter the User ID this ticket is for.</CreateFormSub>

            <div>
              <CreateFormInput
                value={createFormUserId}
                onChange={e => {
                  setCreateFormUserId(e.target.value);
                  setRoleChoice(null);
                  setSelectedRole(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSubmitCreate();
                }}
                placeholder="Enter User ID"
              />
            </div>

            {roleChoice && (
              <RoleChoiceCard>
                <RoleChoiceTitle>
                  This User ID is both a parent and a teacher
                </RoleChoiceTitle>
                <RoleChoiceSub>Who is this ticket for?</RoleChoiceSub>
                <RoleChoiceRow>
                  {roleChoice.roles.map(role => (
                    <RolePill
                      key={role}
                      selected={selectedRole === role}
                      onClick={() => setSelectedRole(role)}
                    >
                      {ROLE_LABELS[role] || role}
                    </RolePill>
                  ))}
                </RoleChoiceRow>
              </RoleChoiceCard>
            )}

            {createError && <CreateFormError>{createError}</CreateFormError>}

            <CreateFormActions>
              <BtnPrimary
                onClick={handleSubmitCreate}
                disabled={
                  creating || !createFormUserId.trim() || (roleChoice && !selectedRole)
                }
              >
                {creating ? 'Creating...' : 'Create ticket'}
              </BtnPrimary>
              <BtnSecondary onClick={handleCancelCreate}>Cancel</BtnSecondary>
            </CreateFormActions>
          </CreateFormInner>
        </CreateFormPane>
      ) : activeTicketId ? (
        <ThreadView
          ticketId={activeTicketId}
          ticketDetail={ticketDetail}
          detailError={ticketDetailError}
          onRefreshDetail={() => getTicketDetail(activeTicketId)}
        />
      ) : (
        <EmptyState>
          <Icon name="chat" size={48} color={SA_COLORS.TEXT_TERTIARY} />
          <EmptyTitle>No ticket selected</EmptyTitle>
          <EmptySub>
            Search for a user and select a ticket to view the conversation, or create a
            new ticket.
          </EmptySub>
        </EmptyState>
      )}
    </Workspace>
  );
};

export default InternalTickets;
